import React, { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Card from 'components/Shared/Card/Card';
import ExtendBtn from 'components/Shared/ExtendBtn/ExtendBtn';
import MerchantMoreInfo from 'components/Shared/Merchant/MoreInfo/MerchantMoreInfo';
import { colors, fontSize } from 'styles/cp';

export type Props = {
  companyName?: string;
  companyLogoUrl?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyWebAddr?: string;
  companyAddress?: Record<string, string>;
  number: string;
  showCompanyHeader?: boolean;
  alwaysExpanded?: boolean;
  noCard?: boolean;
};

const ContactMerchantCard = (props: Props) => {
  const {
    companyName,
    companyLogoUrl,
    companyEmail,
    companyPhone,
    companyWebAddr,
    companyAddress,
    number,
    showCompanyHeader,
    alwaysExpanded = false,
    noCard = false,
  } = props;

  const intl = useIntl();
  const [isContactMerchantOpen, setIisContactMerchantOpen] = useState(alwaysExpanded);

  const getExtendBtn = () => {
    return (
      <ExtendBtn
        opened={isContactMerchantOpen}
        handler={() => {
          setIisContactMerchantOpen(!isContactMerchantOpen);
        }}
        display="block"
        ariaLabel={intl.formatMessage({
          id: 'CONTACT_MERCHANT_MORE_INFO',
          defaultMessage: 'More contact info',
        })}
      />
    );
  };
  const moreInfo = {
    companyEmail,
    companyPhone,
    companyWebAddr,
    companyAddress,
    number,
  };
  // we should peak the info in this order
  let visibleContactInfo;
  if (moreInfo.companyEmail) {
    visibleContactInfo = (
      <div className="c cp-link">
        <FormattedMessage id="SETTINGS_EMAIL" defaultMessage="Email" />
        <span>:&nbsp;</span>
        <a
          href={`mailto:${moreInfo.companyEmail}?subject=${encodeURIComponent(
            `Question about invoice ${moreInfo.number}`
          )}`}
          className="contact contact-link"
        >
          {moreInfo.companyEmail}
        </a>
      </div>
    );
    delete moreInfo.companyEmail;
  } else if (moreInfo.companyPhone) {
    visibleContactInfo = (
      <div className="c cp-link">
        <FormattedMessage id="SETTINGS_PHONE" defaultMessage="Phone" />
        <span>:&nbsp;</span>
        <a href={`tel:${moreInfo.companyPhone}`} className="contact phone-number">
          {moreInfo.companyPhone}
        </a>
      </div>
    );
    delete moreInfo.companyPhone;
  } else if (moreInfo.companyWebAddr) {
    visibleContactInfo = (
      <div className="c cp-link">
        <FormattedMessage id="SETTINGS_WEB" defaultMessage="Web" />
        <span>:&nbsp;</span>
        <a
          href={
            moreInfo.companyWebAddr.indexOf('http') > -1
              ? moreInfo.companyWebAddr
              : 'http://' + moreInfo.companyWebAddr
          }
          target="_blank"
          rel="noopener noreferrer"
          className="contact contact-link"
        >
          {moreInfo.companyWebAddr}
        </a>
      </div>
    );
    delete moreInfo.companyWebAddr;
  } else if (moreInfo.companyAddress) {
    const {
      street1 = '',
      city = '',
      stateOrRegion = '',
      postalCode = '',
    } = moreInfo.companyAddress;
    const companyAddress = `${street1}, ${city}, ${stateOrRegion} ${postalCode}`;
    visibleContactInfo = (
      <div className="c cp-link">
        <FormattedMessage id="SETTINGS_ADDRESS" defaultMessage="Address" />
        <span>:&nbsp;</span>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURI(companyAddress)}`}
          rel="noopener noreferrer"
          className="contact contact-link"
          target="_blank"
        >
          {companyAddress}
        </a>
      </div>
    );
    delete moreInfo.companyAddress;
  }

  // if nothing left to show in the more info collapsible dont show it
  const shouldShowMoreInfo =
    moreInfo.companyPhone ||
    moreInfo.companyEmail ||
    moreInfo.companyWebAddr ||
    (moreInfo.companyAddress &&
      moreInfo.companyAddress.city &&
      moreInfo.companyAddress.stateOrRegion);
  return (
    <Fragment>
      <style jsx>{`
        .company-header {
          padding: 16px 20px 0 20px;
        }

        .company-logo {
          margin-bottom: 12px;

          img {
            max-height: 60px;
            max-width: 100%;
          }
        }

        .company-name {
          font-family: AvenirNextforINTUIT-Demi;
          font-size: ${fontSize.xs};
          color: ${colors.darkGray};
        }
        .business-details {
          font-size: ${fontSize.xs};
          margin-bottom: 12px;
        }

        .w {
          padding: 10px 18px;
          position: relative;

          .c-n {
            font-family: AvenirNextforINTUIT-Medium;
            padding: 8px 0 0 0;
          }

          > label {
            display: block;
          }
        }

        .w-open {
          padding: 0 18px;

          max-height: ${isContactMerchantOpen ? '250px' : 0};
          transition: all 0.35s ease-in-out;
          overflow: hidden;

          :global(svg) {
            transition: all 0.15s linear;
          }

          :global(.contact-info) {
            padding-bottom: 10px;
            display: flex;
            a {
              text-decoration: none;
              cursor: pointer;
              color: ${colors.blue};
            }
          }
        }

        :global(.c) {
          font-size: 14px;
          font-family: AvenirNextforINTUIT-Regular;
        }

        :global(.c a) {
          text-decoration: none;
          color: inherit;

          &:hover {
            color: ${colors.blue};
          }
        }

        :global(.contact.contact-link) {
        }
      `}</style>

      {noCard ? (
        <div className="contact-merchant-content">
          {showCompanyHeader && companyName && (
            <div className="company-header">
              {companyLogoUrl && (
                <div className="company-logo">
                  <img src={companyLogoUrl} alt={companyName} />
                </div>
              )}
              <div className="company-name">{companyName}</div>
            </div>
          )}
          <div className="w">
            <div className="c-n">
              {showCompanyHeader ? (
                <div className="business-details">
                  <FormattedMessage id="COF_BUSINESS_DETAILS" defaultMessage="Business details" />
                </div>
              ) : (
                <FormattedMessage id="MERCHANT_DETAILS" defaultMessage="Business details" />
              )}
            </div>
            {visibleContactInfo}
            {shouldShowMoreInfo && !alwaysExpanded && (
              <div style={{ display: 'flex' }}>{getExtendBtn()}</div>
            )}
          </div>
          {shouldShowMoreInfo && (alwaysExpanded || isContactMerchantOpen) && (
            <div className="w-open">
              <MerchantMoreInfo {...moreInfo} />
            </div>
          )}
        </div>
      ) : (
        <Card>
          {showCompanyHeader && companyName && (
            <div className="company-header">
              {companyLogoUrl && (
                <div className="company-logo">
                  <img src={companyLogoUrl} alt={companyName} />
                </div>
              )}
              <div className="company-name">{companyName}</div>
            </div>
          )}
          <div className="w">
            <div className="c-n">
              {showCompanyHeader ? (
                <div className="business-details">
                  <FormattedMessage id="COF_BUSINESS_DETAILS" defaultMessage="Business details" />
                </div>
              ) : (
                <FormattedMessage id="MERCHANT_DETAILS" defaultMessage="Business details" />
              )}
            </div>
            {visibleContactInfo}
            {shouldShowMoreInfo && !alwaysExpanded && (
              <div style={{ display: 'flex' }}>{getExtendBtn()}</div>
            )}
          </div>
          {shouldShowMoreInfo && (alwaysExpanded || isContactMerchantOpen) && (
            <div className="w-open">
              <MerchantMoreInfo {...moreInfo} />
            </div>
          )}
        </Card>
      )}
    </Fragment>
  );
};

export default ContactMerchantCard;
